import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, Plus } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  PRONTIDAO_LABEL, SITUACAO_CONSULTA_LABEL, TIPO_CONSULTA_LABEL, type Clinica,
} from '@/lib/types'
import {
  addDias, fimMes, formatarDataCurta, formatarDataLonga, fromISO, hojeISO,
  hora, inicioMes, inicioSemana, nomeMes, toISO,
} from '@/lib/datas'
import { listClinicas } from '@/services/clinicas'
import { listConsultas, type ConsultaComPaciente } from '@/services/consultas'

type Visao = 'dia' | 'semana' | 'mes' | 'lista'

export default function AgendaPage() {
  const { current } = useAuth()
  const [visao, setVisao] = useState<Visao>('dia')
  const [ref, setRef] = useState(hojeISO())
  const [clinicaId, setClinicaId] = useState('todas')
  const [clinicas, setClinicas] = useState<Clinica[]>([])
  const [consultas, setConsultas] = useState<ConsultaComPaciente[]>([])
  const [loading, setLoading] = useState(true)

  const intervalo = useMemo(() => {
    if (visao === 'dia') return { de: ref, ate: ref }
    if (visao === 'semana') { const de = inicioSemana(ref); return { de, ate: addDias(de, 6) } }
    if (visao === 'mes') return { de: inicioMes(ref), ate: fimMes(ref) }
    return { de: ref, ate: addDias(ref, 30) } // lista: próximos 30 dias
  }, [visao, ref])

  const carregar = useCallback(async () => {
    if (!current) return
    setLoading(true)
    try {
      const [cs, cons] = await Promise.all([
        listClinicas(current.workspace.id),
        listConsultas({ workspaceId: current.workspace.id, de: intervalo.de, ate: intervalo.ate, clinicaId }),
      ])
      setClinicas(cs)
      setConsultas(cons)
    } finally {
      setLoading(false)
    }
  }, [current, intervalo, clinicaId])

  useEffect(() => { carregar() }, [carregar])

  function navegar(dir: 1 | -1) {
    if (visao === 'dia' || visao === 'lista') setRef(addDias(ref, dir * (visao === 'lista' ? 30 : 1)))
    else if (visao === 'semana') setRef(addDias(ref, dir * 7))
    else {
      const d = fromISO(inicioMes(ref)); d.setMonth(d.getMonth() + dir); setRef(toISO(d))
    }
  }

  const tituloPeriodo =
    visao === 'dia' ? formatarDataLonga(ref)
    : visao === 'semana' ? `${formatarDataCurta(intervalo.de)} – ${formatarDataCurta(intervalo.ate)}`
    : visao === 'mes' ? nomeMes(ref)
    : `Próximos 30 dias`

  const corClinica = (id: string | null) => clinicas.find(c => c.id === id)?.cor ?? 'var(--muted)'
  const nomeClinica = (id: string | null) => clinicas.find(c => c.id === id)?.nome

  function CardConsulta({ c }: { c: ConsultaComPaciente }) {
    const cancelada = c.situacao === 'cancelada' || c.situacao === 'faltou'
    return (
      <Link
        to={`/agenda/nova?id=${c.id}`}
        className="card"
        style={{ padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'center', opacity: cancelada ? 0.55 : 1 }}
      >
        <span aria-hidden style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: corClinica(c.clinica_id) }} />
        <div style={{ minWidth: 52, fontWeight: 700, fontSize: 15 }}>{hora(c.hora_inicio)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>{c.paciente_nome}</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span>{TIPO_CONSULTA_LABEL[c.tipo]}</span>
            {nomeClinica(c.clinica_id) && <span>· {nomeClinica(c.clinica_id)}</span>}
            {c.motivo && <span>· {c.motivo}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          <span className={`badge ${c.situacao === 'realizada' ? 'badge-ok' : cancelada ? 'badge-danger' : 'badge-info'}`}>
            {SITUACAO_CONSULTA_LABEL[c.situacao]}
          </span>
          {!cancelada && c.status_prontidao !== 'nao_iniciada' && (
            <span className={`badge ${c.status_prontidao === 'pronta' ? 'badge-ok' : c.status_prontidao === 'bloqueada' ? 'badge-danger' : 'badge-warn'}`}>
              {PRONTIDAO_LABEL[c.status_prontidao]}
            </span>
          )}
        </div>
      </Link>
    )
  }

  const porDia = useMemo(() => {
    const m = new Map<string, ConsultaComPaciente[]>()
    consultas.forEach(c => {
      const arr = m.get(c.data) ?? []
      arr.push(c)
      m.set(c.data, arr)
    })
    return m
  }, [consultas])

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <h1 className="page-title">Agenda</h1>
        <Link to="/agenda/nova" className="btn-primary">
          <Plus size={16} aria-hidden /> Nova consulta
        </Link>
      </header>

      <div className="card" style={{ padding: 12, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['dia', 'semana', 'mes', 'lista'] as Visao[]).map(v => (
            <button
              key={v} onClick={() => setVisao(v)}
              aria-pressed={visao === v}
              className="badge"
              style={{
                cursor: 'pointer', fontSize: 13, padding: '7px 13px', border: '1px solid',
                background: visao === v ? 'var(--primary-soft)' : 'var(--card)',
                color: visao === v ? 'var(--primary-deep)' : 'var(--text-secondary)',
                borderColor: visao === v ? 'var(--primary)' : 'var(--border-input)',
                textTransform: 'capitalize',
              }}
            >
              {v === 'mes' ? 'Mês' : v}
            </button>
          ))}
          <select
            className="field-input" style={{ width: 'auto', minHeight: 34, fontSize: 13, marginLeft: 'auto' }}
            value={clinicaId} onChange={e => setClinicaId(e.target.value)} aria-label="Filtrar por clínica"
          >
            <option value="todas">Todas as clínicas</option>
            {clinicas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn-secondary" style={{ minHeight: 36, padding: '4px 10px' }} onClick={() => navegar(-1)} aria-label="Período anterior">
            <ChevronLeft size={16} aria-hidden />
          </button>
          <button className="btn-secondary" style={{ minHeight: 36, padding: '4px 12px', fontSize: 13 }} onClick={() => setRef(hojeISO())}>
            Hoje
          </button>
          <button className="btn-secondary" style={{ minHeight: 36, padding: '4px 10px' }} onClick={() => navegar(1)} aria-label="Próximo período">
            <ChevronRight size={16} aria-hidden />
          </button>
          <strong style={{ fontSize: 14.5, textTransform: 'capitalize' }}>{tituloPeriodo}</strong>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      ) : consultas.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <CalendarDays size={26} aria-hidden style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Nenhuma consulta no período</div>
        </div>
      ) : visao === 'dia' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {consultas.map(c => <CardConsulta key={c.id} c={c} />)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[...porDia.entries()].map(([dia, lista]) => (
            <section key={dia}>
              <h2 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-secondary)', margin: '0 0 8px', textTransform: 'capitalize' }}>
                {formatarDataLonga(dia)}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {lista.map(c => <CardConsulta key={c.id} c={c} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
